import Image from 'next/image';
import Link from 'next/link';

import { ISanitizedCompany } from 'types/company';

import { useEmailSignIn } from 'hooks/auth/sign';

import { AuthForm } from './AuthForm';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthGateStore } from 'stores/authGateStore';

interface IProps {
  switchSection(): void;
  company?: ISanitizedCompany;
}

export const SignIn = ({ switchSection, company }: IProps) => {
  const submitLabel = 'Log-in with Email';
  const authStatus = useAuthGateStore(state => state.status);
  const { emailSignInHandler, pending } = useEmailSignIn();

  const shouldPreventInteraction =
    authStatus === 'checking' || authStatus === 'redirecting';

  return (
    <article>
      <header className="flex flex-col items-center">
        <p className="text-sm font-[500]">
          <span className="text-black opacity-50 dark:text-white">
            New User? &nbsp;
          </span>

          <button
            type="button"
            className="cursor-pointer bg-transparent p-0 text-carnationRed hover:underline"
            onClick={switchSection}
            disabled={shouldPreventInteraction}
          >
            Register
          </button>
        </p>

        {!!company?.logoUrl && !!company?.logoDarkUrl && (
          <>
            <div className="relative mt-6 block h-[45px] w-full dark:hidden">
              <Image
                src={company.logoUrl}
                alt={`${company.name} logo`}
                fill
                sizes="(max-width: 640px) 100vw, 400px"
                preload
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </div>

            <div className="relative mt-6 hidden h-[45px] w-full dark:block">
              <Image
                src={company.logoDarkUrl}
                alt={`${company.name} logo`}
                fill
                sizes="(max-width: 640px) 100vw, 400px"
                preload
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </div>
          </>
        )}

        <h2 className="mb-2 mt-10 text-center text-[46px] font-bold leading-tight text-carnationRed">
          Login
        </h2>

        <p className="mb-9 text-center text-lg font-thin">
          Hi there! Sign-in with your account and start enjoying your company
          stories.
        </p>
      </header>

      <div>
        <GoogleSignInButton />
      </div>

      <div className="my-8 flex flex-row items-center justify-between">
        <div className="flex-grow border-b border-b-[#C4C4C4]" />

        <p className="mx-2 text-[10px] font-semibold uppercase text-[#C4C4C4]">
          or
        </p>

        <div className="flex-grow border-b border-b-[#C4C4C4]" />
      </div>

      <AuthForm
        onSubmit={emailSignInHandler}
        submitLabel={submitLabel}
        loading={pending}
        shouldPreventInteraction={shouldPreventInteraction}
      />

      <div className="mt-4 text-right">
        <Link
          href="/forgot-password"
          className="text-sm text-carnationRed hover:underline"
          onClick={e => {
            if (shouldPreventInteraction) {
              e.preventDefault();
            }
          }}
        >
          Forgot Password?
        </Link>
      </div>
    </article>
  );
};
